import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconCheckDouble } from '@optiaxiom/icons';
import {
  Badge,
  Button,
  Group,
  Heading,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from '@optiaxiom/react';
import type { ExpandedState, RowSelectionState } from '@tanstack/react-table';

import { useNamespacedNavigate, useWorkflowUx } from '@frontend/hooks';

import {
  useAcknowledgeBadge,
  useActionRequiredCount,
  useBadgeCount,
  useNotificationActions,
  useNotificationsUrlParams,
  useNotificationTracking,
  useOffsetNotificationList,
  useUnreadCount,
} from '../hooks';
import type { NotificationRow, NotificationTab } from '../types';
import type { HitlTaskDialogState } from '../utils';
import {
  formatUnreadCount,
  getDeepLinkPath,
  isHitlNotification,
  toHitlDialogState,
} from '../utils';

import { NotificationTable } from './table-view/NotificationTable';
import { HitlTaskDialog } from './HitlTaskDialog';
import { NotificationBulkActionsBar } from './NotificationBulkActionsBar';
import { NotificationFilterBar } from './NotificationFilterBar';

export function NotificationCenterPage() {
  const navigate = useNamespacedNavigate();
  const { mutate: markSeen } = useAcknowledgeBadge();
  const { params, updateParams } = useNotificationsUrlParams();
  const t = useNotificationTracking();
  // Real badge value at landing — `ignoreViewingSuppression` bypasses the
  // on-/notifications zeroing in useBadgeCount so we capture what the user saw.
  const { data: badgeCount } = useBadgeCount({
    grouped: true,
    ignoreViewingSuppression: true,
  });

  // On landing: once the badge query resolves, fire "Inbox Viewed" with the real
  // count and THEN acknowledge the badge. Sequencing the ack after the read keeps
  // markSeen from zeroing the count we report (both use the badge-count cache key).
  // Ref-guarded so it runs exactly once per mount.
  const landedRef = useRef(false);
  useEffect(() => {
    if (landedRef.current || badgeCount === undefined) return;
    landedRef.current = true;
    t.inboxViewed(badgeCount);
    markSeen();
  }, [badgeCount, t, markSeen]);

  // Acknowledge again when leaving the page.
  useEffect(() => {
    return () => markSeen();
  }, [markSeen]);
  const { hitlActionRequired } = useWorkflowUx();
  const { page, pageSize, tab, status, agentId, category, priority, time, view } = params;
  // Gated on the flag as well as the tab so a stale `?tab=action-required` link
  // cannot filter the list once the flag is off — with the tab bar gone there
  // would be no way to get back to the unfiltered view.
  const actionRequired = hitlActionRequired && tab === 'action-required';

  const { data, isPending } = useOffsetNotificationList({
    page,
    pageSize,
    // The tab counts pending items regardless of read state, so filtering the
    // list by it would show fewer rows than the tab's own label promises.
    status: actionRequired ? 'all' : status,
    agentId,
    // The Action Required tab picks its own set of types, so a category filter
    // on top of it can only ever narrow the list to nothing.
    category: actionRequired ? undefined : category,
    priority,
    time,
    grouped: true,
    actionRequired,
  });
  const { data: unreadCount } = useUnreadCount({ grouped: true });
  const { data: actionRequiredCount } = useActionRequiredCount({
    enabled: hitlActionRequired,
    grouped: true,
  });
  const actions = useNotificationActions();

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const pagination = data?.pagination;

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [hitlState, setHitlState] = useState<HitlTaskDialogState>(null);

  useEffect(() => {
    setRowSelection({});
    setExpanded({});
  }, [page, tab, view]);

  const selectedIds = useMemo(() => {
    const selected = Object.keys(rowSelection).filter(k => rowSelection[k]);
    const leafIds = new Set(
      rows.flatMap(r => (r.notification ? [r.id] : r.subRows.map(sr => sr.id)))
    );
    return selected.filter(id => leafIds.has(id));
  }, [rowSelection, rows]);

  const handleMarkRead = useCallback(
    (id: string) =>
      actions.markAsRead.mutate(id, { onSuccess: () => t.markedAsRead(1, 'overflow_menu') }),
    [actions.markAsRead, t]
  );
  const handleMarkUnread = useCallback(
    (id: string) => actions.markAsUnread.mutate(id),
    [actions.markAsUnread]
  );
  const handleDelete = useCallback(
    (id: string, type: string) => {
      actions.deleteNotification.mutate(id, {
        onSuccess: () => t.dismissed(type, 'overflow_menu'),
      });
    },
    [actions.deleteNotification, t]
  );

  const handleGroupMarkRead = useCallback(
    (groupId: string) => {
      actions.bulkMarkAsRead.mutate(
        { group_id: groupId },
        { onSuccess: data => t.markedAsRead(data.updated_count, 'overflow_menu') }
      );
    },
    [actions.bulkMarkAsRead, t]
  );
  const handleGroupDelete = useCallback(
    (groupId: string, type: string) => {
      actions.bulkDelete.mutate(
        { group_id: groupId },
        { onSuccess: () => t.dismissed(type, 'overflow_menu') }
      );
    },
    [actions.bulkDelete, t]
  );

  const handleBulkRead = useCallback(() => {
    actions.bulkMarkAsRead.mutate(
      { ids: selectedIds },
      { onSuccess: data => t.bulkAction(data.updated_count, 'mark_as_read') }
    );
    setRowSelection({});
  }, [actions.bulkMarkAsRead, selectedIds, t]);

  const handleBulkUnread = useCallback(() => {
    actions.bulkMarkAsUnread.mutate(
      { ids: selectedIds },
      { onSuccess: data => t.bulkAction(data.updated_count, 'mark_as_unread') }
    );
    setRowSelection({});
  }, [actions.bulkMarkAsUnread, selectedIds, t]);

  const handleBulkDismiss = useCallback(() => {
    actions.bulkDelete.mutate(
      { ids: selectedIds },
      { onSuccess: data => t.bulkAction(data.updated_count, 'dismiss') }
    );
    setRowSelection({});
  }, [actions.bulkDelete, selectedIds, t]);

  const handleActivate = useCallback(
    (row: NotificationRow) => {
      const notification = row.notification;
      if (!notification) return;
      t.clicked(notification.type, 'inbox');
      if (!notification.is_read) {
        actions.markAsRead.mutate(notification.id);
      }
      if (isHitlNotification(notification)) {
        setHitlState(toHitlDialogState(notification));
        return;
      }
      navigate(getDeepLinkPath(notification));
    },
    [actions.markAsRead, navigate, t]
  );

  const handleMarkAllRead = useCallback(() => {
    actions.bulkMarkAsRead.mutate(undefined, {
      onSuccess: data => t.markedAsRead(data.updated_count, 'inbox'),
    });
  }, [actions.bulkMarkAsRead, t]);

  const content = (
    <Group flexDirection="column" gap="16">
      <NotificationFilterBar
        status={status}
        hideStatus={actionRequired}
        agentId={agentId}
        category={category}
        hideCategory={actionRequired}
        priority={priority}
        time={time}
        view={view}
        onStatusChange={s => {
          updateParams({ status: s });
          if (s !== 'all') t.filterApplied('status');
        }}
        onAgentChange={a => {
          updateParams({ agentId: a });
          if (a !== undefined) t.filterApplied('agent');
        }}
        onCategoryChange={c => {
          updateParams({ category: c });
          if (c !== undefined) t.filterApplied('category');
        }}
        onPriorityChange={p => {
          updateParams({ priority: p });
          if (p !== undefined) t.filterApplied('priority');
        }}
        onTimeChange={tm => {
          updateParams({ time: tm });
          if (tm !== undefined) t.filterApplied('time');
        }}
        onViewChange={v => updateParams({ view: v })}
        actions={
          <Button
            size="md"
            appearance="default"
            icon={<IconCheckDouble />}
            onClick={handleMarkAllRead}
          >
            Mark all as read
          </Button>
        }
      />

      {selectedIds.length > 0 && (
        <NotificationBulkActionsBar
          selectedCount={selectedIds.length}
          onMarkRead={handleBulkRead}
          onMarkUnread={handleBulkUnread}
          onDismiss={handleBulkDismiss}
        />
      )}

      {view === 'table' && (
        <NotificationTable
          rows={rows}
          pagination={pagination}
          isLoading={isPending}
          page={page}
          pageSize={pageSize}
          onPageChange={p => updateParams({ page: p })}
          onPageSizeChange={ps => updateParams({ pageSize: ps })}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          expanded={expanded}
          onExpandedChange={setExpanded}
          onActivate={handleActivate}
          onMarkRead={handleMarkRead}
          onMarkUnread={handleMarkUnread}
          onDelete={handleDelete}
          onGroupMarkRead={handleGroupMarkRead}
          onGroupDelete={handleGroupDelete}
        />
      )}

      {!isPending && rows.length === 0 && view === 'table' && (
        <Group justifyContent="center" py="48">
          <Text color="fg.tertiary">
            {actionRequired ? 'Nothing needs your action' : 'No notifications'}
          </Text>
        </Group>
      )}
    </Group>
  );

  const actionRequiredLabel = formatUnreadCount(actionRequiredCount);

  return (
    <Group flexDirection="column" gap="16" px="32" py="24">
      <Group gap="12" alignItems="center">
        <Heading color="fg.default" level="1">
          Notification Center
        </Heading>
        {unreadCount != null && unreadCount > 0 && (
          <Badge intent="information">{unreadCount} unread</Badge>
        )}
      </Group>

      <HitlTaskDialog state={hitlState} onClose={() => setHitlState(null)} />

      {hitlActionRequired ? (
        <Tabs
          display="flex"
          flexDirection="column"
          gap="16"
          onValueChange={value => updateParams({ tab: value as NotificationTab })}
          value={tab}
        >
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="action-required">
              {actionRequiredLabel ? `Action Required (${actionRequiredLabel})` : 'Action Required'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">{content}</TabsContent>
          <TabsContent value="action-required">{content}</TabsContent>
        </Tabs>
      ) : (
        // Without the flag there is only one view, so the tab bar is dropped
        // entirely rather than left as a single orphan tab.
        content
      )}
    </Group>
  );
}
